import { useEffect } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { AuthPage } from './pages/AuthPage.tsx'
import { AdminPage } from './pages/AdminPage.tsx'
import { ShoppingPage } from './pages/ShoppingPage.tsx'
import { ShoppingListPage } from './pages/ShoppingListPage.tsx'
import { PlannerPage } from './pages/PlannerPage.tsx'
import { HealthPage } from './pages/HealthPage.tsx'
import { WishesPage, WishListPage } from './pages/WishesPage.tsx'
import { MoviesPage } from './pages/MoviesPage.tsx'
import { WalletPage } from './pages/WalletPage.tsx'
import { RecipesPage, RecipePage } from './pages/RecipesPage.tsx'
import { BudgetPage } from './pages/BudgetPage.tsx'
import { DocumentsPage } from './pages/DocumentsPage.tsx'
import { FeedPage } from './pages/FeedPage.tsx'
import { AppNav } from './components/AppNav.tsx'
import { CallView } from './components/CallView.tsx'
import { ChatList } from './components/ChatList.tsx'
import { ChatView } from './components/ChatView.tsx'
import { Toast } from './components/Toast.tsx'
import { useAuth } from './store/auth.ts'
import { useChats } from './store/chats.ts'
import { useCall, setCallIdentity } from './store/call.ts'
import { onServerEvent, setReconnectHandler } from './realtime/socket.ts'
import { refreshPushSubscription } from './lib/push.ts'

/**
 * Раскладка как в мессенджерах: на широком экране список и переписка рядом,
 * на телефоне — два отдельных экрана. Признак «переписка открыта» вынесен
 * в класс на обёртке, чтобы всё решалось стилями, без ветвления вёрстки.
 */
function Messenger() {
  const { chatId } = useParams<{ chatId?: string }>()

  return (
    <div className={'layout' + (chatId ? ' has-open-chat' : '')}>
      <ChatList />
      <main className="main">
        {chatId ? (
          <ChatView />
        ) : (
          <div className="no-chat">
            <p>Выбери переписку слева</p>
          </div>
        )}
      </main>
    </div>
  )
}

/**
 * Общая обёртка разделов: переход между ними всегда под рукой.
 *
 * Список переписок нужен не только в мессенджере — непрочитанные видны
 * на значке из любого раздела, поэтому загружается он здесь, один раз.
 *
 * Открытая на телефоне переписка занимает весь экран: строка разделов
 * внизу отнимала бы место у ленты сообщений и лезла бы под клавиатуру.
 */
function Shell() {
  const location = useLocation()
  const loadChats = useChats((s) => s.loadChats)
  const me = useAuth((s) => s.user)

  useEffect(() => {
    void loadChats()
  }, [loadChats])

  /**
   * Досылаем то, что не ушло.
   *
   * Три повода попробовать: приложение открылось, связь вернулась,
   * человек вернулся к вкладке. Последнее важнее, чем кажется: телефон
   * усыпляет вкладку в кармане, и «сеть появилась» там никто не сообщит.
   */
  /**
   * Связь вернулась — забираем пропущенное.
   *
   * События за время обрыва не догоняют: сервер отправил их в мёртвое
   * соединение и забыл. Без этого сообщения появлялись только после
   * того, как человек сам переоткрывал переписку.
   */
  useEffect(() => {
    setReconnectHandler(() => void useChats.getState().catchUp())

    /**
     * Заодно напоминаем серверу о подписке на уведомления.
     *
     * Он удаляет её после нескольких неудачных отправок — например,
     * если телефон был долго выключен. Браузер при этом считает себя
     * подписанным, и человек уверен, что уведомления придут.
     */
    void refreshPushSubscription()
  }, [])

  useEffect(() => {
    const flush = () => void useChats.getState().flushOutbox()

    flush()
    window.addEventListener('online', flush)
    document.addEventListener('visibilitychange', flush)

    // Раз в полминуты на случай, если ни одно из событий не пришло:
    // мобильные браузеры сообщают о возвращении сети не всегда
    const timer = setInterval(flush, 30_000)

    return () => {
      window.removeEventListener('online', flush)
      document.removeEventListener('visibilitychange', flush)
      clearInterval(timer)
    }
  }, [])

  /**
   * Звонки слушаются здесь, а не в переписке: позвонить могут, когда
   * человек стоит в списке покупок, и трубка должна зазвонить всё равно.
   */
  useEffect(() => {
    if (!me) return
    setCallIdentity(me.id)

    const call = useCall.getState()

    return onServerEvent((event) => {
      if (event.type === 'call:incoming') {
        useCall.getState().onIncoming(event.call, event.callerName, event.chatTitle)
      } else if (event.type === 'call:updated') {
        useCall.getState().onUpdated(event.call)
      } else if (event.type === 'call:ended') {
        useCall.getState().onEnded(event.callId)
      } else if (event.type === 'call:signal') {
        void useCall.getState().onSignal(event.callId, event.fromUserId, event.payload)
      }
    })

    void call
  }, [me])

  const chatOpen = location.pathname.startsWith('/chat/')

  return (
    <div className={'app-shell' + (chatOpen ? ' has-open-chat' : '')}>
      <AppNav />
      <div className="app-body">
        <Outlet />
      </div>
      <CallView />
      <Toast />
    </div>
  )
}

export function App() {
  const status = useAuth((s) => s.status)
  const restore = useAuth((s) => s.restore)

  useEffect(() => {
    void restore()
  }, [restore])

  if (status === 'checking') {
    return (
      <div className="center-screen">
        <div className="spinner" />
      </div>
    )
  }

  if (status === 'anonymous') return <AuthPage />

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route path="/" element={<Messenger />} />
        <Route path="/chat/:chatId" element={<Messenger />} />
        <Route path="/shopping" element={<ShoppingPage />} />
        <Route path="/shopping/:listId" element={<ShoppingListPage />} />
        <Route path="/planner" element={<PlannerPage />} />
        <Route path="/health" element={<HealthPage />} />
        <Route path="/wishes" element={<WishesPage />} />
        <Route path="/wishes/:listId" element={<WishListPage />} />
        <Route path="/movies" element={<MoviesPage />} />
        <Route path="/wallet" element={<WalletPage />} />
        <Route path="/wallet/ticket/:ticketId" element={<WalletPage />} />
        <Route path="/recipes" element={<RecipesPage />} />
        <Route path="/recipes/:recipeId" element={<RecipePage />} />
        <Route path="/budget" element={<BudgetPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/feed" element={<FeedPage />} />
      </Route>
      {/* Наблюдение за приложением — отдельный экран без строки разделов */}
      <Route path="/admin" element={<AdminPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
